// components/RotaBuilder/LeaveSelectionDialog.tsx - ENHANCED WITH TIL HOURS
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  TextField,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Schedule as ClockIcon,
  Person as UserIcon,
  Warning as AlertIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertColor } from '@mui/material/Alert';

interface Personnel {
  _id: string;
  name: string;
  email: string;
  roles: string[];
  leaveEntitlement?: {
    annualLeaveTotal: number;
    annualLeaveUsed: number;
    timeInLieuHours: number;
    timeInLieuUsed: number;
    annualLeaveYear: number;
  };
  currentLeaveBalance?: {
    annualLeave: number;
    timeInLieu: number;
    timeInLieuDays: number;
    totalAvailableDays: number;
  };
}

interface LeaveSelectionDialogProps {
  open: boolean;
  personnelId: string;
  selectedDate: Date | null;
  isSuperAdmin?: boolean;
  onConfirm: (leaveType: 'annual-leave' | 'time-lieu', hours?: number, timeSlot?: { startTime: string; endTime: string }) => void;
  onCancel: () => void;
}

const LeaveSelectionDialog: React.FC<LeaveSelectionDialogProps> = ({
  open,
  personnelId,
  selectedDate,
  isSuperAdmin = false,
  onConfirm,
  onCancel
}) => {
  const [personnel, setPersonnel] = useState<Personnel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState<'annual-leave' | 'time-lieu'>('annual-leave');
  const [tilStartTime, setTilStartTime] = useState<string>('10:00');
  const [tilEndTime, setTilEndTime] = useState<string>('13:00');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({
  open: false,
  message: '',
  severity: 'info'
});

  // Calculate TIL hours from time selection
  const calculateTilHours = (): number => {
    const [startHour, startMin] = tilStartTime.split(':').map(Number);
    const [endHour, endMin] = tilEndTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    const diffMinutes = endMinutes - startMinutes;
    
    if (diffMinutes <= 0) return 0;
    
    // Round to nearest 0.5 hours
    return Math.round((diffMinutes / 60) * 2) / 2;
  };

  const tilHours = calculateTilHours();

  // Fetch personnel data when dialog opens
  useEffect(() => {
    if (open && personnelId) {
      console.log('LeaveDialog: isSuperAdmin =', isSuperAdmin);
      fetchPersonnelData();
    }
  }, [open, personnelId]);

  const fetchPersonnelData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/api/personnel/${personnelId}`);
      setPersonnel(response.data);
      
      // Default to annual leave if available, otherwise TIL
      if (response.data.currentLeaveBalance?.annualLeave > 0) {
        setSelectedLeaveType('annual-leave');
      } else if (response.data.currentLeaveBalance?.timeInLieu > 0) {
        setSelectedLeaveType('time-lieu');
      } else {
        setSelectedLeaveType('annual-leave'); // Let them see the warning
      }
      
    } catch (err: any) {
      console.error('Error fetching personnel data:', err);
      setError(err.response?.data?.message || 'Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

const handleConfirm = async () => {
  if (!personnelId || !selectedDate) {
    setSnackbar({ open: true, message: 'Missing staff or date', severity: 'error' });
    return;
  }

  try {
    setLoading(true);

    // Hours only for TIL
    const hours = selectedLeaveType === 'time-lieu' ? calculateTilHours() : undefined;

    // ✅ FIXED: Simple date string with NO timezone conversion
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Payload for your current controller (expects leaveType/date)
    // PLUS normalized fields for the Mongoose model (type/dateStart/hoursRequested)
    const payload: any = {
      personnelId,
      leaveType: selectedLeaveType,        // controller expects this
      date: dateStr,                       // ✅ FIXED: Simple string, no ISO conversion
      hours,                               // controller expects this for TIL
      reason: 'Requested via RotaBuilder',
      status: 'pending',

      // also include model-friendly keys so either implementation works
      type: selectedLeaveType,
      dateStart: dateStr,                  // ✅ FIXED: Simple string, no ISO conversion
      hoursRequested: hours,
      startTime: selectedLeaveType === 'time-lieu' ? tilStartTime : undefined,
      endTime: selectedLeaveType === 'time-lieu' ? tilEndTime : undefined,
      adminOverride: isSuperAdmin && selectedLeaveType === 'time-lieu' && tilHours > (balance?.timeInLieu || 0)
    };

    console.log('📤 Submitting leave request:', payload);
    const res = await axiosInstance.post('/api/leave-requests', payload);
    console.log('✅ Leave request submitted:', res.data);

    setSnackbar({ open: true, message: 'Leave request submitted for approval!', severity: 'success' });

    // Bubble up so the calendar can show the local pending overlay
    onConfirm(
      selectedLeaveType,
      hours,
      selectedLeaveType === 'time-lieu'
        ? { startTime: tilStartTime, endTime: tilEndTime }
        : undefined
    );
  } catch (err: any) {
    console.error('❌ Error submitting leave request:', err);
    setSnackbar({
      open: true,
      message: err.response?.data?.message || 'Error submitting request',
      severity: 'error'
    });
  } finally {
    setLoading(false);
  }
};

  const formatDate = (date: Date | null) => {
    if (!date) return 'No date selected';
    return date.toLocaleDateString('en-IE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading leave data...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const balance = personnel?.currentLeaveBalance;
  const hasAnnualLeave = balance?.annualLeave && balance.annualLeave > 0;
  const hasTIL = balance?.timeInLieu && balance.timeInLieu >= tilHours;
  const tilDays = Math.floor((balance?.timeInLieu || 0) / 8);
  const remainingTilAfter = (balance?.timeInLieu || 0) - tilHours;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: 24, color: '#3A9188' }} />
          <Typography variant="h6">
            Select Leave Type
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {personnel && (
          <>
            {/* Date and Person Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <UserIcon sx={{ fontSize: 16 }} />
                    <Typography variant="subtitle2">Staff Member</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {personnel.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarIcon sx={{ fontSize: 16 }} />
                    <Typography variant="subtitle2">Leave Date</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {formatDate(selectedDate)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Leave Balances */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Your Leave Balances
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: hasAnnualLeave ? 'success.main' : 'warning.main',
                    borderRadius: 1,
                    bgcolor: hasAnnualLeave ? 'success.50' : 'warning.50'
                  }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Annual Leave
                    </Typography>
                    <Typography variant="h4" color={hasAnnualLeave ? 'success.main' : 'warning.main'}>
                      {balance?.annualLeave || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      days remaining ({personnel.leaveEntitlement?.annualLeaveTotal || 0} total)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: hasTIL ? 'info.main' : 'grey.300',
                    borderRadius: 1,
                    bgcolor: hasTIL ? 'info.50' : 'grey.50'
                  }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Time in Lieu
                    </Typography>
                    <Typography variant="h4" color={hasTIL ? 'info.main' : 'text.secondary'}>
                      {tilDays}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      days available ({balance?.timeInLieu || 0} hours)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Leave Type Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Choose Leave Type
              </Typography>
              
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedLeaveType}
                  onChange={(e) => setSelectedLeaveType(e.target.value as 'annual-leave' | 'time-lieu')}
                >
                  <FormControlLabel
                    value="annual-leave"
                    control={<Radio />}
                    disabled={!hasAnnualLeave}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>
                          Use Annual Leave (1 day)
                        </Typography>
                        {hasAnnualLeave ? (
                          <Chip label="Available" size="small" color="success" />
                        ) : (
                          <Chip label="No days left" size="small" color="warning" />
                        )}
                      </Box>
                    }
                  />
                  
                  <FormControlLabel
                    value="time-lieu"
                    control={<Radio />}
                    disabled={!isSuperAdmin && (!balance?.timeInLieu || balance.timeInLieu < 2)}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography>
                            Use Time in Lieu
                          </Typography>
                          {(balance?.timeInLieu || 0) >= 2 ? (
                            <Chip label={`${balance?.timeInLieu || 0}h available`} size="small" color="info" />
                          ) : (
                            <Chip label="Insufficient hours" size="small" color="default" />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {/* TIL Time Selection */}
              {selectedLeaveType === 'time-lieu' && (
                <Box sx={{ mt: 2, ml: 4, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClockIcon sx={{ fontSize: 16 }} />
                    Select Time Period
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Start Time"
                        type="time"
                        value={tilStartTime}
                        onChange={(e) => setTilStartTime(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          step: 300 // 5 minute intervals
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="End Time"
                        type="time"
                        value={tilEndTime}
                        onChange={(e) => setTilEndTime(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          step: 300 // 5 minute intervals
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="info.main">
                          {tilHours}h
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Duration
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Remaining after: {remainingTilAfter}h ({Math.floor(remainingTilAfter / 8)} days)
                    </Typography>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    💡 Perfect for "10am-1pm" or "2pm-6pm" partial days. Use annual leave for full days.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Warnings */}
            {!hasAnnualLeave && (!balance?.timeInLieu || balance.timeInLieu < 2) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AlertIcon sx={{ fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold">
                    No leave available
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  You don't have sufficient annual leave or time in lieu. 
                  This leave day will result in a negative balance that requires admin approval.
                </Typography>
              </Alert>
            )}

            {selectedLeaveType === 'annual-leave' && !hasAnnualLeave && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Insufficient Annual Leave:</strong> You have {balance?.annualLeave || 0} days remaining.
                  Taking this day will result in a negative balance.
                </Typography>
              </Alert>
            )}

            {selectedLeaveType === 'time-lieu' && tilHours <= 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Invalid Time Selection:</strong> End time must be after start time.
                </Typography>
              </Alert>
            )}

            {selectedLeaveType === 'time-lieu' && tilHours > (balance?.timeInLieu || 0) && (
              isSuperAdmin ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Superadmin override:</strong> You have {balance?.timeInLieu || 0}h available — this will result in a negative TIL balance of {remainingTilAfter}h.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Insufficient Time in Lieu:</strong> You need {tilHours} hours but only have {balance?.timeInLieu || 0} hours available.
                  </Typography>
                </Alert>
              )
            )}

            {/* Success Preview */}
            {((selectedLeaveType === 'annual-leave' && hasAnnualLeave) ||
              (selectedLeaveType === 'time-lieu' && tilHours > 0 && (isSuperAdmin || tilHours <= (balance?.timeInLieu || 0)))) && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>✅ Leave Available:</strong> {' '}
                  {selectedLeaveType === 'annual-leave' 
                    ? `Using 1 day of annual leave. ${(balance?.annualLeave || 0) - 1} days remaining.`
                    : `Using ${tilHours} hours TIL (${tilStartTime}-${tilEndTime}). ${remainingTilAfter} hours remaining.`
                  }
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedLeaveType === 'time-lieu' && (tilHours <= 0 || (!isSuperAdmin && tilHours > (balance?.timeInLieu || 0)))}
          sx={{ 
            bgcolor: selectedLeaveType === 'time-lieu' ? '#ca8a04' : '#3A9188',
            '&:hover': { 
              bgcolor: selectedLeaveType === 'time-lieu' ? '#a16207' : '#2D7A72' 
            }
          }}
        >
          Apply {selectedLeaveType === 'time-lieu' ? `TIL (${tilStartTime}-${tilEndTime})` : 'Annual Leave'}
        </Button>
      </DialogActions>
         <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
};

export default LeaveSelectionDialog;